import * as Sentry from '@sentry/react';

const { VITE_API_FETCH_TIMEOUT_SEC } = import.meta.env;

// Default timeout: 30 seconds (increased from 3s for mobile networks)
const DEFAULT_FETCH_TIMEOUT = 30000;
export const FETCH_TIMEOUT = VITE_API_FETCH_TIMEOUT_SEC
  ? Number(VITE_API_FETCH_TIMEOUT_SEC) * 1000
  : DEFAULT_FETCH_TIMEOUT;

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

// Error messages
export const TIMEOUT_ERROR = 'FETCH_TIMEOUT';
const NETWORK_ERROR = 'NETWORK_ERROR';

/**
 * Checks if the error is a transient network error that should be retried
 */
function isRetryableError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    // Mobile Safari throws "Load failed" for various transient network issues
    // Also retry on AbortError (timeout) and generic network failures
    return (
      message.includes('load failed') ||
      message.includes('network') ||
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      error.name === 'AbortError' ||
      error.name === 'TypeError'
    );
  }
  return false;
}

/**
 * Detects if the current browser is Mobile Safari
 */
function isMobileSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) && /Safari/.test(ua) && !/Chrome/.test(ua);
}

/**
 * Gets network information for debugging context
 */
function getNetworkContext(): Record<string, unknown> {
  const context: Record<string, unknown> = {
    online: typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
    timestamp: new Date().toISOString(),
  };

  // Add connection info if available (Network Information API)
  if (typeof navigator !== 'undefined' && 'connection' in navigator) {
    const connection = navigator.connection as {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      saveData?: boolean;
    };
    if (connection) {
      context.effectiveType = connection.effectiveType;
      context.downlink = connection.downlink;
      context.rtt = connection.rtt;
      context.saveData = connection.saveData;
    }
  }

  return context;
}

/**
 * Delays execution for the specified milliseconds
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Creates a custom fetch function with timeout, retry logic, and Mobile Safari handling
 *
 * Features:
 * - Configurable timeout (default 30s for mobile networks)
 * - Automatic retry for transient network errors (up to 2 retries)
 * - Special handling for Mobile Safari "Load failed" errors
 * - Enhanced Sentry error context with network information
 * - Request ID tracking from response headers
 */
export async function createCustomFetch(
  uri: RequestInfo | URL,
  options?: RequestInit,
): Promise<Response> {
  let lastError: Error | null = null;
  const isMobileSafariBrowser = isMobileSafari();

  // eslint-disable-next-line no-plusplus
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    try {
      // Set up timeout
      timeoutId = setTimeout(() => {
        controller.abort();
      }, FETCH_TIMEOUT);

      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(uri, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Track request ID for debugging
      const requestId = response.headers.get('x-request-id');
      if (requestId) {
        Sentry.setTag('request_id', requestId);
      }

      return response;
    } catch (error) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      const isAborted = controller.signal.aborted;
      const errorInstance =
        error instanceof Error ? error : new Error(String(error));

      // Determine error type for better context
      let errorType = NETWORK_ERROR;
      if (isAborted) {
        errorType = TIMEOUT_ERROR;
      } else if (
        isMobileSafariBrowser &&
        errorInstance.message.toLowerCase().includes('load failed')
      ) {
        errorType = 'MOBILE_SAFARI_LOAD_FAILED';
      }

      // Add context to Sentry
      Sentry.setContext('fetchError', {
        errorType,
        attempt: attempt + 1,
        maxRetries: MAX_RETRIES,
        timeout: FETCH_TIMEOUT,
        uri: typeof uri === 'string' ? uri : uri.toString(),
        isMobileSafari: isMobileSafariBrowser,
        isAborted,
        errorMessage: errorInstance.message,
        errorName: errorInstance.name,
        ...getNetworkContext(),
      });

      lastError = errorInstance;

      // Check if we should retry
      const shouldRetry = attempt < MAX_RETRIES && isRetryableError(error);

      if (shouldRetry) {
        // Exponential backoff: 1s, 2s
        const backoffDelay = RETRY_DELAY_MS * (attempt + 1);
        // eslint-disable-next-line no-await-in-loop
        await delay(backoffDelay);
        // eslint-disable-next-line no-continue
        continue;
      }

      // Create a more descriptive error for timeout
      if (isAborted) {
        const timeoutError = new Error(
          `Request timed out after ${FETCH_TIMEOUT}ms (attempt ${attempt + 1}/${MAX_RETRIES + 1})`,
        );
        timeoutError.name = TIMEOUT_ERROR;
        throw timeoutError;
      }

      throw errorInstance;
    }
  }

  // This should not be reached, but just in case
  throw lastError || new Error('Fetch failed after all retries');
}

// Export helpers for apps that need custom fetch logic (e.g., ServiceWorker integration)
export {
  isRetryableError,
  isMobileSafari,
  getNetworkContext,
  delay,
  MAX_RETRIES,
  RETRY_DELAY_MS,
};

export default createCustomFetch;
